import CopyAllIcon from "@mui/icons-material/CopyAll";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useLocation } from "react-router-dom";

import usePackageDeal from "hooks/usePackageDeal";
import { CurrencyContext } from "../useContext";
import confetti from "canvas-confetti";
import { useTranslation } from "i18n";
import { roundPrice } from "utils/packageDealPrice";
import { checkISAdminOrEmployee, duration } from "utils/Utils";
import Payment from "components/payment";
import AddRemark from "components/remark";
import "../booking/redesign.css";
import "./style.css";

const BookingInfo = ({ setClientSecret, setOpenCheckout }) => {
  const { locale, t } = useTranslation();
  const { currency } = useContext(CurrencyContext);
  const [expand, setExpand] = useState(false);
  const [copy, setCopy] = useState(false);
  const [paymentType, setPaymentType] = useState();
  const [charge, setCharge] = useState(0);
  const [openSegments, setOpenSegments] = useState({});

  const { state } = useLocation();
  const { deal: packageDeal, loading: packageDealLoading } = usePackageDeal(
    state?.packageDealId,
  );

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }

    const palette = ["#22c55e", "#16a34a", "#86efac", "#facc15", "#ffffff", "#f15e22"];
    const burst = (origin, extra = {}) => {
      confetti({
        particleCount: 90,
        spread: 76,
        startVelocity: 36,
        ticks: 220,
        gravity: 0.9,
        origin,
        colors: palette,
        disableForReducedMotion: true,
        ...extra,
      });
    };

    burst({ x: 0.5, y: 0.22 }, { spread: 100, particleCount: 120 });
    const side = window.setTimeout(() => {
      burst({ x: 0.08, y: 0.55 }, { angle: 60, particleCount: 46, spread: 55 });
      burst({ x: 0.92, y: 0.55 }, { angle: 120, particleCount: 46, spread: 55 });
    }, 160);

    return () => window.clearTimeout(side);
  }, []);

  const handleOnSuccessPaymentRequest = (client_secret) => {
    setClientSecret(client_secret);
    setOpenCheckout(true);
  };

  const getPaymentCharge = () => {
    const getCharge = charge * 1;
    if (getCharge > 0) {
      return ((getCharge * state?.getTotalPrice) / 100).toFixed(2);
    }
    return false;
  };

  const getPriceForTraveler = (price) => {
    let editedPrice = price;
    if (state?.fareAdjustment) {
      editedPrice = (
        price * 1 +
        state?.fareAdjustment / state?.travelerPricing?.length
      ).toFixed(2);
    }
    return editedPrice;
  };

  const referenceId = state?.overview?.data?.associatedRecords?.[0]?.reference;
  const getTravelerTypeLabel = (type) => t(`booking.travelerTypes.${type}`);
  const getRoomTypeLabel = (type) =>
    ["single", "double", "triple", "quad"].includes(type)
      ? t(`booking.labels.${type}`)
      : type;

  const offer = state?.overview?.data?.flightOffers?.[0];
  const itineraries = offer?.itineraries || [];
  const travelers = state?.overview?.data?.travelers || [];
  const dictionaries = state?.dictionaries || {};
  const travelerPricings = state?.travelerPricing || offer?.travelerPricings || [];

  const getLocation = (iata) => dictionaries?.locations?.[iata];
  const getCabin = (segmentId) =>
    travelerPricings?.[0]?.fareDetailsBySegment?.find(
      (sege) => sege?.segmentId == segmentId,
    )?.cabin;
  const getAirlineLogo = (carrierCode) => {
    const logos = dictionaries?.airlineLogos?.[carrierCode];
    return logos?.[0]?.tailImage || logos?.[0]?.squareImage;
  };
  const getTraveler = (id) => travelers.find((traveler) => traveler?.id == id);
  const travelerInitials = (traveler) =>
    `${traveler?.name?.firstName?.charAt(0) || ""}${
      traveler?.name?.lastName?.charAt(0) || ""
    }`;
  const travelerShort = (traveler) =>
    traveler
      ? `${traveler.name?.firstName}.${traveler.name?.lastName?.charAt(0)}`
      : "";
  const toggleSegment = (key) => {
    setOpenSegments((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const baggageRows =
    travelerPricings?.filter(
      (sege) => sege?.fareDetailsBySegment?.[0]?.includedCheckedBags?.quantity > 0,
    ) || [];
  const noBaggage = travelerPricings?.some(
    (sege) => sege?.fareDetailsBySegment?.[0]?.includedCheckedBags?.quantity === 0,
  );
  const seatRows =
    travelerPricings?.flatMap((item) =>
      (item?.fareDetailsBySegment || [])
        .filter((fare) => fare?.additionalServices?.chargeableSeatNumber)
        .map((fare) => ({ item, fare })),
    ) || [];

  const groupedTravelers = Object.entries(
    (travelerPricings || []).reduce((acc, traveler) => {
      const type = traveler?.travelerType || "ADULT";
      if (!acc[type]) acc[type] = { count: 0, total: 0 };
      acc[type].count += 1;
      acc[type].total += Number(getPriceForTraveler(traveler?.price?.total)) || 0;
      return acc;
    }, {}),
  );

  const finalTotal =
    charge > 0 && getPaymentCharge()
      ? roundPrice((state?.getTotalPrice ?? 0) * 1 + getPaymentCharge() * 1)
      : roundPrice(state?.getTotalPrice ?? 0);

  return (
    <div className="reservation bkg-v2">
      <div className="booking-info reservation-content">
        <div className="bk-confirm-main">
          <header className="bk-confirm-hero">
            <div className="bk-confirm-hero-badge" aria-hidden="true">
              <svg viewBox="0 0 52 52">
                <circle className="bk-confirm-hero-ring" cx="26" cy="26" r="23" />
                <path
                  className="bk-confirm-hero-check"
                  d="M16.5 27.2 23 33.5 35.5 18.8"
                />
              </svg>
            </div>
            <div className="bk-confirm-hero-copy">
              <p>{t("confirmation.eyebrow")}</p>
              <h1>{t("confirmation.title")}</h1>
              <span>
                {t(
                  state?.packageDeal
                    ? "confirmation.subtitlePackage"
                    : "confirmation.subtitleFlight",
                )}
              </span>
            </div>
            {referenceId && (
              <div className="bk-confirm-ref">
                <em>{t("confirmation.referenceId")}</em>
                <div className="bk-confirm-ref-row">
                  <code>{referenceId}</code>
                  <CopyToClipboard
                    text={referenceId}
                    onCopy={() => setCopy(true)}
                  >
                    <Tooltip
                      title={copy ? t("confirmation.copied") : t("confirmation.copy")}
                    >
                      <IconButton
                        size="small"
                        aria-label={t("confirmation.copyReference")}
                      >
                        <CopyAllIcon />
                      </IconButton>
                    </Tooltip>
                  </CopyToClipboard>
                </div>
              </div>
            )}
          </header>

          <section className="bk-s4-block bk-confirm-pay">
            <Payment
              setPaymentType={setPaymentType}
              setCharge={setCharge}
              paymentType={paymentType}
              totalPrice={state?.getTotalPrice}
              totalAmount={state?.getTotalPrice * 1}
              id={state?.overview?.data?.id}
              tikkieUrl={state?.tikkieUrl}
              currency={currency}
              handleOnSuccessPaymentRequest={handleOnSuccessPaymentRequest}
              referenceId={referenceId}
            />
          </section>

          {state?.packageDeal && (
            <section className="bk-s4-block bk-confirm-package-note">
              <p>{t("confirmation.packageDealBooking")}</p>
              <span>
                {t("confirmation.packageIncluded", {
                  currency: currency?.sign ?? " € ",
                  price:
                    state?.packagePrice != null
                      ? Number(state.packagePrice).toLocaleString()
                      : "",
                })}
              </span>
              {state?.packageRoomBreakdown && (
                <small>
                  {t("confirmation.roomConfiguration")}:{" "}
                  {[
                    state.packageRoomBreakdown.single > 0 &&
                      `${state.packageRoomBreakdown.single}× ${getRoomTypeLabel("single")}`,
                    state.packageRoomBreakdown.double > 0 &&
                      `${state.packageRoomBreakdown.double}× ${getRoomTypeLabel("double")}`,
                    state.packageRoomBreakdown.triple > 0 &&
                      `${state.packageRoomBreakdown.triple}× ${getRoomTypeLabel("triple")}`,
                    state.packageRoomBreakdown.quad > 0 &&
                      `${state.packageRoomBreakdown.quad}× ${getRoomTypeLabel("quad")}`,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </small>
              )}
            </section>
          )}

          {state?.packageDealId && (
            <section className="bk-s4-block">
              <p className="bk-s4-block-title">
                {t("confirmation.packageTourDetails")}
              </p>
              {packageDealLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress
                    sx={{ color: "var(--primary-color)" }}
                    size={36}
                  />
                </Box>
              ) : packageDeal ? (
                <>
                  <div className="bk-confirm-package-head">
                    {packageDeal.imageUrls?.[0] && (
                      <div
                        className="bk-confirm-package-image"
                        style={{
                          backgroundImage: `url(${packageDeal.imageUrls[0]})`,
                        }}
                      />
                    )}
                    <div>
                      <h3>{packageDeal.title}</h3>
                      <span>
                        {packageDeal.duration}{" "}
                        {packageDeal.duration === 1
                          ? t("packageDeals.night")
                          : t("packageDeals.nights")}
                      </span>
                    </div>
                  </div>
                  {packageDeal.description && (
                    <p className="bk-s1-people-copy">{packageDeal.description}</p>
                  )}
                  {packageDeal.itinerary?.length > 0 && (
                    <div>
                      <p className="bk-s4-block-title">
                        {t("packageDeals.travelProgramme")}
                      </p>
                      {packageDeal.itinerary.map((day, index) => (
                        <div
                          key={day.day ?? index}
                          className="bk-confirm-day"
                        >
                          <strong>
                            {t("packageDeals.day", { day: day.day })}
                          </strong>
                          <div>
                            {day.title && <p>{day.title}</p>}
                            {day.description && <span>{day.description}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {(packageDeal.included?.length > 0 ||
                    packageDeal.excluded?.length > 0) && (
                    <div className="bk-confirm-lists">
                      {packageDeal.included?.length > 0 && (
                        <div>
                          <p>{t("packageDeals.included")}</p>
                          <ul>
                            {packageDeal.included.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {packageDeal.excluded?.length > 0 && (
                        <div>
                          <p>{t("packageDeals.notIncluded")}</p>
                          <ul>
                            {packageDeal.excluded.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : null}
            </section>
          )}

          <section className="bk-s1-trip">
            <div className="bk-s1-trip-head">
              <div>
                <p className="bk-s1-kicker">{t("booking.labels.itinerary")}</p>
                <h2>{t("confirmation.flightDetails")}</h2>
              </div>
              <span className="bk-s1-trip-count">
                {itineraries.length}{" "}
                {itineraries.length === 1
                  ? t("booking.labels.flight")
                  : t("booking.labels.flights")}
              </span>
            </div>

            <div className="bk-s1-itin-list">
              {itineraries.map((item, itineraryIndex) => {
                const stopCount = Math.max((item?.segments?.length || 1) - 1, 0);

                return (
                  <article className="bk-s1-itin" key={itineraryIndex}>
                    <div className="bk-s1-itin-top">
                      <span className="bk-s1-itin-badge">
                        {itineraryIndex === 0
                          ? t("booking.labels.departure")
                          : t("booking.labels.return")}
                      </span>
                      <span className="bk-s1-itin-meta">
                        {stopCount === 0
                          ? t("booking.labels.direct")
                          : t("booking.labels.stopCount", { count: stopCount })}
                        <span aria-hidden="true">·</span>
                        {t("booking.labels.duration")} {duration(item?.duration)}
                      </span>
                    </div>

                    {item?.segments?.map((segment, segmentIndex) => {
                      const key = `${itineraryIndex}-${segmentIndex}`;
                      const isOpen = Boolean(openSegments[key]);
                      const from = getLocation(segment?.departure?.iataCode);
                      const to = getLocation(segment?.arrival?.iataCode);
                      const logo = getAirlineLogo(segment?.carrierCode);
                      const cabin = getCabin(segment?.id);

                      return (
                        <div className="bk-s1-seg" key={key}>
                          {item.segments.length > 1 && (
                            <p className="bk-s1-seg-cities">
                              {from?.city || segment?.departure?.iataCode}
                              <span aria-hidden="true">→</span>
                              {to?.city || segment?.arrival?.iataCode}
                            </p>
                          )}

                          <div className="bk-s1-seg-route">
                            <div className="bk-s1-seg-point">
                              <strong>
                                {moment(segment?.departure?.at).format("H:mm")}
                              </strong>
                              <span>{segment?.departure?.iataCode}</span>
                              <small>
                                {moment(segment?.departure?.at)
                                  .locale(locale)
                                  .format("ddd D MMM")}
                              </small>
                            </div>
                            <div className="bk-s1-seg-line">
                              <span>
                                {duration(segment?.duration, item?.duration)}
                              </span>
                            </div>
                            <div className="bk-s1-seg-point is-end">
                              <strong>
                                {moment(segment?.arrival?.at).format("H:mm")}
                              </strong>
                              <span>{segment?.arrival?.iataCode}</span>
                              <small>
                                {moment(segment?.arrival?.at)
                                  .locale(locale)
                                  .format("ddd D MMM")}
                              </small>
                            </div>
                          </div>

                          <button
                            type="button"
                            className={`bk-s1-seg-toggle${isOpen ? " is-open" : ""}`}
                            onClick={() => toggleSegment(key)}
                          >
                            <span className="bk-s1-seg-carrier">
                              {logo ? (
                                <img className="bk-s1-airline" src={logo} alt="" />
                              ) : null}
                              {cabin && <em>{cabin}</em>}
                              <span>
                                {t("booking.labels.flightNo")} {segment?.number}
                              </span>
                            </span>
                            <span className="bk-s1-seg-more">
                              {isOpen
                                ? t("booking.labels.hideFlightDetails")
                                : t("booking.labels.showFlightDetails")}
                            </span>
                          </button>

                          {isOpen && (
                            <div className="bk-s1-seg-details">
                              <p>
                                <span>{t("booking.labels.airline")}</span>
                                {dictionaries?.carriers?.[segment?.carrierCode] ||
                                  segment?.carrierCode}
                              </p>
                              <p>
                                <span>{t("booking.labels.aircraft")}</span>
                                {segment?.aircraft?.code || "—"}
                              </p>
                              <p>
                                <span>
                                  {from?.airport || segment?.departure?.iataCode}
                                </span>
                                <span aria-hidden="true">→</span>
                                {to?.airport || segment?.arrival?.iataCode}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </article>
                );
              })}
            </div>
          </section>

          <section className="bk-s4-block">
            <div className="bk-s1-trip-head">
              <div>
                <p className="bk-s1-kicker">{t("booking.labels.passengers")}</p>
                <h2>{t("confirmation.travelersDetails")}</h2>
              </div>
              <span className="bk-s1-trip-count">
                {travelers.length}{" "}
                {travelers.length === 1
                  ? t("booking.labels.passenger")
                  : t("booking.labels.passengers")}
              </span>
            </div>

            <div className="bk-s4-travelers">
              {travelers.map((traveler) => {
                const travelerType = travelerPricings?.find(
                  (trav) => trav.travelerId == traveler?.id,
                )?.travelerType;

                return (
                  <article className="bk-s4-person" key={traveler?.id}>
                    <span className="bk-s4-avatar">
                      {travelerInitials(traveler)}
                    </span>
                    <div className="bk-s4-person-copy">
                      <div className="bk-s4-person-top">
                        <p>
                          {traveler?.name?.firstName} {traveler?.name?.lastName}
                        </p>
                        {travelerType && (
                          <span>{getTravelerTypeLabel(travelerType)}</span>
                        )}
                      </div>
                      <p className="bk-s4-person-meta">
                        {traveler?.gender} · {traveler?.dateOfBirth}
                      </p>
                      {(traveler?.contact?.emailAddress ||
                        traveler?.contact?.phones?.[0]?.number) && (
                        <div className="bk-s4-person-contacts">
                          {traveler?.contact?.emailAddress && (
                            <span>{traveler.contact.emailAddress}</span>
                          )}
                          {traveler?.contact?.phones?.[0]?.number && (
                            <span>{traveler.contact.phones[0].number}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>

            {noBaggage && travelerPricings?.[0]?.fareDetailsBySegment?.[0]
              ?.includedCheckedBags?.quantity === 0 && (
              <p className="bk-s1-people-copy" style={{ marginTop: 12 }}>
                {t("booking.messages.noBaggage")}
              </p>
            )}

            {baggageRows.length > 0 && (
              <>
                <p className="bk-s4-block-title" style={{ marginTop: 18 }}>
                  {t("booking.labels.baggage")}
                </p>
                <div className="bk-s4-lines">
                  {baggageRows.map((sege) => {
                    const traveler = getTraveler(sege?.travelerId);
                    const quantity =
                      sege?.fareDetailsBySegment?.[0]?.includedCheckedBags
                        ?.quantity;
                    return (
                      <div className="bk-s4-line" key={sege?.travelerId}>
                        <div>
                          <p>
                            {quantity}× {t("booking.labels.personalItem")}
                          </p>
                          <span>
                            {quantity} × 23 kg
                          </span>
                        </div>
                        <strong>{travelerShort(traveler)}</strong>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {seatRows.length > 0 && (
              <>
                <p className="bk-s4-block-title" style={{ marginTop: 18 }}>
                  {t("booking.labels.seating")}
                </p>
                <div className="bk-s4-lines">
                  {seatRows.map(({ item, fare }, index) => {
                    const traveler = getTraveler(item?.travelerId);
                    const segment = itineraries
                      .flatMap((itin) => itin?.segments || [])
                      .find((seg) => seg?.id == fare?.segmentId);
                    return (
                      <div
                        className="bk-s4-line"
                        key={`${item?.travelerId}-${index}`}
                      >
                        <div>
                          <p>
                            {segment?.departure?.iataCode || "—"} —{" "}
                            {segment?.arrival?.iataCode || "—"}
                          </p>
                          <span>{t("booking.labels.specificSeat")}</span>
                        </div>
                        <strong>
                          {travelerShort(traveler)} ·{" "}
                          {fare?.additionalServices?.chargeableSeatNumber}
                        </strong>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </section>

          {checkISAdminOrEmployee() && offer?.source === "GDS" && (
            <section className="bk-s4-block">
              <p className="bk-s4-block-title">{t("confirmation.remarks")}</p>
              <AddRemark
                flightOrderId={state?.overview?.data?.id}
                officeId={state?.officeId}
              />
            </section>
          )}
        </div>

        <aside className="reversation-sideBar-wrapper">
          <div className="reversation-bill">
            <div className="reversation-bill-box bk-bill">
              <div className="bk-bill-head">
                <span className="bk-bill-sky" aria-hidden="true" />
                <div className="bk-bill-head-text">
                  <p>{t("booking.labels.priceSummary")}</p>
                  <span>
                    {travelers.length}{" "}
                    {travelers.length === 1
                      ? t("booking.labels.passenger")
                      : t("booking.labels.passengers")}
                  </span>
                </div>
              </div>
              <div className="reversation-bill-box-items">
                {groupedTravelers.map(([type, group]) => (
                  <div className="reversation-bill-item" key={type}>
                    <p>
                      {getTravelerTypeLabel(type)} ({group.count}x)
                    </p>
                    <p>
                      {currency?.sign}
                      {group.total.toFixed(2)}
                    </p>
                  </div>
                ))}

                <div className="reversation-bill-item reversation-bill-item--tax">
                  <p>{t("booking.labels.taxesAndFees")}</p>
                  <p className="reversation-bill-included">
                    {t("booking.labels.included")}
                  </p>
                </div>

                {travelers.map(
                  (traveler, index) =>
                    travelerPricings[index]?.travelerType !== "HELD_INFANT" &&
                    state?.reverseSeats?.[index]?.[traveler?.id]?.seats && (
                      <div className="reversation-bill-item" key={index}>
                        <p>
                          {t("booking.labels.seatFor", {
                            type: getTravelerTypeLabel(
                              travelerPricings[index]?.travelerType,
                            ),
                            name: travelerShort(traveler),
                          })}
                        </p>
                        <p>
                          {currency?.sign}
                          {state.reverseSeats[index][traveler.id].seats
                            .map((i) => (i?.price === "FREE" ? 0 : +i?.price))
                            .reduce((total, num) => total + num, 0)
                            .toFixed(2)
                            .toString()}
                        </p>
                      </div>
                    ),
                )}

                {state?.packageDeal && state?.packagePrice != null && (
                  <div className="reversation-bill-item">
                    <p>{t("confirmation.packageTour")}</p>
                    <p>
                      {currency?.sign ?? "€ "}
                      {Number(state.packagePrice).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                  </div>
                )}

                {charge > 0 && getPaymentCharge() && (
                  <div className="reversation-bill-item">
                    <p>{t("confirmation.paymentCharge")}</p>
                    <p>
                      {currency?.sign}
                      {getPaymentCharge()}
                    </p>
                  </div>
                )}
              </div>

              <div className="bk-bill-total">
                <p>
                  {t("booking.labels.total", { currency: currency?.currency })}
                </p>
                <p>
                  {currency?.sign}
                  {finalTotal.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </aside>
      </div>

      <div
        className={`bk-pricebar ${expand ? "expand" : ""}`}
        onClick={() => setExpand(!expand)}
      >
        {expand && (
          <div className="bk-pricebar-panel">
            {groupedTravelers.map(([type, group]) => (
              <div className="bk-pricebar-row" key={type}>
                <p>
                  {getTravelerTypeLabel(type)} ({group.count}x)
                </p>
                <p>
                  {currency?.sign}
                  {group.total.toFixed(2)}
                </p>
              </div>
            ))}
            {state?.packageDeal && state?.packagePrice != null && (
              <div className="bk-pricebar-row">
                <p>{t("confirmation.packageTour")}</p>
                <p>
                  {currency?.sign ?? "€ "}
                  {Number(state.packagePrice).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
              </div>
            )}
          </div>
        )}
        <div className="bk-pricebar-bar">
          <div>
            <p>{t("booking.labels.total", { currency: currency?.currency })}</p>
            <span>
              {travelers.length}{" "}
              {travelers.length === 1
                ? t("booking.labels.passenger")
                : t("booking.labels.passengers")}
            </span>
          </div>
          <strong>
            {currency?.sign}
            {finalTotal.toFixed(2)}
          </strong>
        </div>
      </div>
    </div>
  );
};

export default BookingInfo;
